import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  applyActionCode,
  reload,
  sendEmailVerification,
  signOut,
} from 'firebase/auth';
import Button from '../components/Button';
import { auth } from '../lib/firebase';

type VerifyState = 'checking' | 'awaiting' | 'success' | 'error';

const cardStyle: React.CSSProperties = {
  width: 'min(560px, 100%)',
  border: '1px solid rgba(255,255,255,.12)',
  borderRadius: 14,
  padding: '1rem',
  background: 'rgba(8,14,30,.68)',
  display: 'grid',
  gap: '.65rem',
};

const layoutStyle: React.CSSProperties = {
  minHeight: '70vh',
  display: 'grid',
  placeItems: 'center',
  padding: '2rem 1rem',
};

const actionsStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '.55rem',
  marginTop: '.25rem',
};

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const oobCode = params.get('oobCode');
  const mode = params.get('mode');

  const [state, setState] = useState<VerifyState>('checking');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const continueUrl = `${window.location.origin}/verify-email`;
  const currentEmail = auth.currentUser?.email || '';

  useEffect(() => {
    let active = true;
    if (mode !== 'verifyEmail' || !oobCode) {
      setState('awaiting');
      return () => {
        active = false;
      };
    }

    (async () => {
      try {
        await applyActionCode(auth, oobCode);
        if (!active) return;
        setState('success');
        setMessage('Email verified. You can continue to your dashboard.');
      } catch (err: any) {
        if (!active) return;
        setState('error');
        setMessage(err?.message || 'This verification link is invalid or expired.');
      }
    })();

    return () => {
      active = false;
    };
  }, [mode, oobCode]);

  const handleResend = async () => {
    const current = auth.currentUser;
    if (!current) {
      setState('awaiting');
      setMessage('Sign in with your invited email first, then resend verification.');
      return;
    }

    setBusy(true);
    try {
      await sendEmailVerification(current, {
        url: continueUrl,
        handleCodeInApp: true,
      });
      setState('awaiting');
      setMessage(
        `Verification email sent to ${current.email || 'your email address'}.`,
      );
    } catch (err: any) {
      setState('error');
      setMessage(err?.message || 'Could not send verification email. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleContinue = async () => {
    const current = auth.currentUser;
    if (!current) {
      navigate('/', { replace: true });
      return;
    }

    setBusy(true);
    try {
      await reload(current);
      if (current.emailVerified) {
        navigate('/dashboard/home', { replace: true });
        return;
      }
      setState('awaiting');
      setMessage('Email is not verified yet. Check your inbox and click the link.');
    } finally {
      setBusy(false);
    }
  };

  const handleReturnToSignIn = async () => {
    try {
      await signOut(auth);
    } catch {
      // noop
    }
    navigate('/', { replace: true });
  };

  return (
    <div style={layoutStyle}>
      <div style={cardStyle}>
        {state === 'checking' ? (
          <>
            <h2 style={{ margin: 0 }}>Verifying your email...</h2>
            <p style={{ margin: 0, color: '#9aa4b2' }}>
              Please wait while we validate your verification link.
            </p>
          </>
        ) : null}

        {state === 'success' ? (
          <>
            <h2 style={{ margin: 0 }}>Email verified</h2>
            <p style={{ margin: 0, color: '#9aa4b2' }}>
              {message || 'Your email is verified.'}
            </p>
            <div style={actionsStyle}>
              <Button type="button" onClick={handleContinue} disabled={busy}>
                Continue
              </Button>
            </div>
          </>
        ) : null}

        {state === 'awaiting' ? (
          <>
            <h2 style={{ margin: 0 }}>Verify your email</h2>
            <p style={{ margin: 0, color: '#9aa4b2' }}>
              {currentEmail
                ? `You are signed in as ${currentEmail}. Verify your email to continue.`
                : 'Sign in with your invited email, then verify your email to continue.'}
            </p>
            {message ? (
              <p style={{ margin: 0, color: '#9fd7ff', fontSize: '.9rem' }}>{message}</p>
            ) : null}
            <div style={actionsStyle}>
              <Button type="button" onClick={handleResend} disabled={busy}>
                {busy ? 'Sending...' : 'Resend verification email'}
              </Button>
              <Button type="button" onClick={handleContinue} disabled={busy}>
                I verified, continue
              </Button>
              <Button type="button" onClick={handleReturnToSignIn} disabled={busy}>
                Return to sign in
              </Button>
            </div>
          </>
        ) : null}

        {state === 'error' ? (
          <>
            <h2 style={{ margin: 0 }}>Verification link error</h2>
            <p style={{ margin: 0, color: '#9aa4b2' }}>
              {message || 'This verification link is invalid or expired.'}
            </p>
            <div style={actionsStyle}>
              <Button type="button" onClick={handleResend} disabled={busy}>
                {busy ? 'Sending...' : 'Resend verification email'}
              </Button>
              <Button type="button" onClick={handleReturnToSignIn} disabled={busy}>
                Return to sign in
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
